import { useRef } from 'react';

import {
  ResourceFormat,
  resourcePreviewUrl,
  ResourceViewBehaviorInput,
} from '../resourceClickBehavior';

const OS_RESOURCE_HANDLERS = ['/file_resource', '/embeddable_resource'];

// A mounted embed owns its loaded URL. Refreshing access must not navigate its iframe
// just because OS issued another temporary token for the same PDF, deck or page.
export default function useResourcePreviewUrl(
  resource: ResourceViewBehaviorInput & { slug?: string },
  { loading = false, error = false, active = true } = {}
) {
  const preview = useRef<{ identity: string; url: string }>();
  const url = resourcePreviewUrl(resource || {});
  const isPdf = resource?.resource_format_name === ResourceFormat.PDF;

  let target = url;
  let isOsHandler = false;
  try {
    const parsed = url ? new URL(url, window.location.origin) : undefined;
    if (parsed && OS_RESOURCE_HANDLERS.includes(parsed.pathname)) {
      isOsHandler = true;
      parsed.searchParams.delete('access_token');
      target = parsed.href;
    }
  } catch {
    // Leave malformed or nonstandard URLs to the existing browser behavior.
  }

  if (!active || error || !url || (!isPdf && !isOsHandler)) {
    preview.current = undefined;
    return active && !loading && !error ? url : undefined;
  }

  const identity = `${resource.slug || ''}:${target}`;

  if (!loading && preview.current?.identity !== identity) {
    preview.current = { identity, url };
  }

  // A new preview waits for authentication; an existing embed survives a refetch.
  return preview.current?.identity === identity ? preview.current.url : undefined;
}
